import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { 
  Plus, Edit2, Trash2, Search, X, 
  Save, ChevronLeft, ChevronRight,
  Download, ToggleLeft, ToggleRight
} from 'lucide-react';
import { toast } from 'sonner';

const AdminBrands = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadBrands();
  }, [pagination.skip, searchQuery]);

  const loadBrands = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: pagination.skip.toString(),
        limit: pagination.limit.toString()
      });
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await fetchWithAuth(`/api/admin/brands?${params}`);
      if (res.ok) {
        const data = await res.json();
        setBrands(data.brands || []);
        setPagination(prev => ({ ...prev, total: data.total || 0 }));
      }
    } catch (error) {
      toast.error('Error al cargar marcas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, skip: 0 }));
    loadBrands();
  };

  const openCreateModal = () => {
    setEditingBrand(null);
    setFormData({
      name: '',
      description: ''
    });
    setShowModal(true);
  };

  const openEditModal = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name || '',
      description: brand.description || ''
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingBrand 
        ? `/api/admin/brands/${editingBrand.id}`
        : '/api/admin/brands';
      const method = editingBrand ? 'PUT' : 'POST';
      
      const res = await fetchWithAuth(url, {
        method,
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || (editingBrand ? 'Marca actualizada' : 'Marca creada'));
        setShowModal(false);
        loadBrands();
      } else {
        const err = await res.json();
        // Handle Pydantic validation errors (array format)
        if (Array.isArray(err.detail)) {
          const errorMsg = err.detail.map(e => e.msg || e.message).join(', ');
          toast.error(errorMsg || 'Error de validación');
        } else {
          toast.error(err.detail || 'Error al guardar');
        }
      }
    } catch (error) {
      toast.error('Error al guardar marca');
    } finally {
      setSaving(false);
    }
  };

  const toggleBrandStatus = async (brand) => {
    try {
      const res = await fetchWithAuth(`/api/admin/brands/${brand.id}/toggle`, {
        method: 'PATCH'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.is_active ? 'Marca activada' : 'Marca desactivada');
        loadBrands();
      }
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const deleteBrand = async (brand) => {
    if (!window.confirm(`¿Eliminar la marca "${brand.name}"?`)) return;
    
    try {
      const res = await fetchWithAuth(`/api/admin/brands/${brand.id}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        loadBrands();
      }
    } catch (error) {
      toast.error('Error al eliminar marca');
    }
  };

  const importFromProducts = async () => {
    setImporting(true);
    try {
      const res = await fetchWithAuth('/api/admin/brands/import-from-products', {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        loadBrands();
      }
    } catch (error) {
      toast.error('Error al importar marcas');
    } finally {
      setImporting(false);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="p-6" data-testid="admin-brands">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Marcas</h1>
          <p className="text-gray-400 text-sm mt-1">
            {pagination.total} marcas registradas
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={importFromProducts}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Download size={18} />
            {importing ? 'Importando...' : 'Importar de Productos'}
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-neon-cyan text-black font-medium rounded-lg hover:bg-neon-cyan/80 transition-colors"
          >
            <Plus size={18} />
            Nueva Marca
          </button>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nombre o país..."
            className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-neon-cyan"
          />
        </div>
      </form>

      {/* Brands Table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-800 text-left">
                <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Nombre</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase hidden md:table-cell">Descripción</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Estado</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                    Cargando...
                  </td>
                </tr>
              ) : brands.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                    No se encontraron marcas
                  </td>
                </tr>
              ) : (
                brands.map((brand) => (
                  <tr key={brand.id} className="hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">{brand.name}</p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="text-gray-400 text-sm truncate max-w-xs">
                        {brand.description || '-'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleBrandStatus(brand)}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
                          brand.is_active 
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        }`}
                      >
                        {brand.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        {brand.is_active ? 'Activa' : 'Inactiva'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(brand)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => deleteBrand(brand)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <p className="text-sm text-gray-400">
              Página {currentPage} de {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip - prev.limit }))}
                disabled={pagination.skip === 0}
                className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                disabled={currentPage >= totalPages}
                className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-900 rounded-xl w-full max-w-lg border border-gray-700 shadow-2xl my-8 flex-shrink-0">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">
                {editingBrand ? 'Editar Marca' : 'Nueva Marca'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-neon-cyan"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Descripción
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-neon-cyan resize-none"
                  placeholder="Descripción opcional de la marca..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-neon-cyan text-black font-medium rounded-lg hover:bg-neon-cyan/80 transition-colors disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBrands;
